/**
 * UpgradeSheet — the checkout flow (Plan 02 D1): plan → coupon
 (`POST /coupons/validate`) → `POST /checkout` → the provider door opens
 (popup; the redirect back lands on `checkout-bridge.html`) → the payment's
 SSE stream drives the success/error states, including `requires_action`
 (3-DS) which keeps the stream open until the provider settles.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { billingClient, BillingError } from "../client/billingClient";
import { invalidateBillingCache } from "../hooks/billingCache";
import { formatMoney } from "../money";
import { useBillingLabels } from "../strings";
import type { CheckoutAnswer, CouponEvaluation, Plan } from "../types";

export interface UpgradeSheetProps {
  open: boolean;
  onClose: () => void;
  /** The plan this round buys (pack or threshold tier). */
  plan: Plan | null;
  /** Called once the SSE stream confirms the payment — the consumer's cue to
   *  refresh its own billing reads (the kit already drops its cache). */
  onSuccess?: (subscriptionId: string | null) => void;
}

type CheckoutPhase =
  | { kind: "idle" }
  | { kind: "opening" }
  | { kind: "awaiting"; payment: CheckoutAnswer }
  | { kind: "success"; subscriptionId: string | null }
  | { kind: "failed"; message: string };

export function UpgradeSheet({ open, onClose, plan, onSuccess }: UpgradeSheetProps) {
  const labels = useBillingLabels();
  const [phase, setPhase] = useState<CheckoutPhase>({ kind: "idle" });
  const [coupon, setCoupon] = useState("");
  const [couponState, setCouponState] = useState<{ checking: boolean; result: CouponEvaluation | null; error: string | null }>({
    checking: false,
    result: null,
    error: null,
  });
  const [simulating, setSimulating] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const popupRef = useRef<Window | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Every open starts a clean round — a stale phase must never survive a reopen.
  useEffect(() => {
    if (open) {
      setPhase({ kind: "idle" });
      setCoupon("");
      setCouponState({ checking: false, result: null, error: null });
    }
  }, [open, plan?.id]);

  // The SSE subscription + the popup are this sheet's resources — dropped on
  // close/unmount so a closed sheet never keeps a socket or a window.
  useEffect(() => {
    if (open) return;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    popupRef.current?.close();
    popupRef.current = null;
  }, [open]);

  useEffect(
    () => () => {
      unsubscribeRef.current?.();
      popupRef.current?.close();
    },
    []
  );

  const price = formatMoney(plan?.prices.usd ?? null, "USD");

  const evaluateCoupon = useCallback(async () => {
    if (!plan || !coupon.trim()) return;
    setCouponState((state) => ({ ...state, checking: true, error: null }));
    try {
      const result = await billingClient.validateCoupon(coupon.trim(), plan.id);
      setCouponState({ checking: false, result, error: null });
    } catch {
      setCouponState({ checking: false, result: null, error: labels.couponInvalid });
    }
  }, [coupon, plan, labels.couponInvalid]);

  const startCheckout = useCallback(async () => {
    if (!plan) return;
    setPhase({ kind: "opening" });
    try {
      const answer = await billingClient.startCheckout(plan.id, { coupon: couponState.result?.code });
      // A `mock://` checkout URL is the local-development provider double's
      // marker — there is no hosted page to open, the dev controls below
      // drive the round trip. A blocked popup degrades to the same tab (the
      // bridge page hands the round trip back either way).
      const popup = answer.checkoutUrl.startsWith("mock://") ? null : window.open(answer.checkoutUrl, "_blank", "noopener");
      popupRef.current = popup;
      setPhase({ kind: "awaiting", payment: answer });
      unsubscribeRef.current?.();
      unsubscribeRef.current = billingClient.subscribePaymentEvents(answer.paymentId, (event) => {
        if (event.status === "completed" || event.status === "paid") {
          setPhase({ kind: "success", subscriptionId: event.subscriptionId });
          // The server's meters/subscription moved — every kit consumer
          // re-reads from the network after this, not from the 5-min cache.
          invalidateBillingCache();
          onSuccessRef.current?.(event.subscriptionId);
          unsubscribeRef.current?.();
          unsubscribeRef.current = null;
        } else if (["failed", "canceled", "expired", "charged_back"].includes(event.status)) {
          setPhase({ kind: "failed", message: event.status });
          unsubscribeRef.current?.();
          unsubscribeRef.current = null;
        }
        // requires_action / processing keep the stream open — 3-DS settles here.
      });
    } catch (err) {
      setPhase({ kind: "failed", message: err instanceof BillingError ? err.message : "The checkout could not start." });
    }
  }, [plan, couponState.result]);

  /** The dev double: post the signed mock webhook the provider page would
   *  have produced, for the outcome the developer is exercising. */
  const simulateMockOutcome = useCallback(async (paymentId: string, outcome: "success" | "failed") => {
    setSimulating(true);
    try {
      await billingClient.simulateMockPayment(paymentId, outcome);
      // The SSE stream delivers the terminal state — nothing to set here.
    } catch (err) {
      setPhase({ kind: "failed", message: err instanceof BillingError ? err.message : "The simulation failed." });
    } finally {
      setSimulating(false);
    }
  }, []);

  if (!open || !plan) return null;

  return (
    <div className="suite-ui-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="suite-ui-dialog suite-billing-dialog" role="dialog" aria-modal="true" aria-label={labels.checkoutTitle(plan.name)}>
        <div className="suite-ui-dialog-header">
          <strong>{labels.checkoutTitle(plan.name)}</strong>
        </div>
        <div className="suite-ui-dialog-body">
          <div className="suite-billing-sheet-body">
            <div className="suite-billing-card">
              <div className="suite-billing-card-head">
                <span className="suite-billing-card-title">{plan.name}</span>
                <span className="suite-billing-card-sub">{plan.description}</span>
              </div>
              <div className="suite-billing-row">
                <span className="suite-billing-row-title">{formatMoney(plan.prices.usd, "USD")}</span>
                <span className="suite-billing-row-hint">{plan.billingCycle === "one_time" ? "one-time" : plan.billingCycle}</span>
              </div>
              {couponState.result && (
                <p className="suite-billing-sheet-subtitle">{labels.couponValid(formatMoney(couponState.result.discount.amount, couponState.result.currency))}</p>
              )}
              {couponState.error && (
                <div className="suite-billing-error">
                  <span>{couponState.error}</span>
                </div>
              )}
              <div className="suite-billing-coupon">
                <input
                  className="suite-billing-input"
                  type="text"
                  value={coupon}
                  placeholder={labels.couponPlaceholder}
                  onChange={(event) => setCoupon(event.target.value)}
                  disabled={phase.kind === "awaiting" || phase.kind === "success"}
                />
                <button
                  type="button"
                  className="suite-billing-button"
                  onClick={() => void evaluateCoupon()}
                  disabled={!coupon.trim() || couponState.checking || phase.kind === "awaiting" || phase.kind === "success"}
                >
                  {labels.couponApply}
                </button>
              </div>
            </div>

            {phase.kind === "awaiting" && (
              <>
                <div className="suite-billing-status">{labels.checkoutPending}</div>
                {phase.payment.provider === "mock" && (
                  <div className="suite-billing-card">
                    <div className="suite-billing-card-head">
                      <span className="suite-billing-card-title">{labels.devSimulateTitle}</span>
                    </div>
                    <div className="suite-billing-row-actions">
                      <button
                        type="button"
                        className="suite-billing-button suite-billing-button--primary"
                        disabled={simulating}
                        onClick={() => void simulateMockOutcome(phase.payment.paymentId, "success")}
                      >
                        {labels.devSimulateSuccess}
                      </button>
                      <button
                        type="button"
                        className="suite-billing-button suite-billing-button--danger"
                        disabled={simulating}
                        onClick={() => void simulateMockOutcome(phase.payment.paymentId, "failed")}
                      >
                        {labels.devSimulateFailure}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            {phase.kind === "success" && <div className="suite-billing-status suite-billing-status--success">{labels.checkoutSuccess}</div>}
            {phase.kind === "failed" && (
              <div className="suite-billing-status suite-billing-status--danger">{labels.checkoutFailed(phase.message)}</div>
            )}
          </div>
        </div>
        <div className="suite-ui-dialog-footer">
          <span className="suite-billing-footer-spacer" />
          {phase.kind === "idle" || phase.kind === "opening" || phase.kind === "failed" ? (
            <button
              type="button"
              className="suite-billing-button suite-billing-button--primary"
              onClick={() => void startCheckout()}
              disabled={phase.kind === "opening" || couponState.checking}
            >
              {labels.payButton(price)}
            </button>
          ) : null}
          <button type="button" className="suite-billing-button" onClick={onClose}>
            {labels.checkoutClose}
          </button>
        </div>
      </div>
    </div>
  );
}
